import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import './Header.css';
import RoleBasedAccess from './RoleBasedAccess.tsx';
import useAuth from '../hooks/useAuth.ts';
import Modal from './Modal.tsx';

const NewHeader = () => {
  const { auth, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle logout with API call
  const handleLogout = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');
      
      if (token) {
        await fetch(`${backendUrl}/api/auth/signout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Always logout locally regardless of API call result
      logout();
      setIsDropdownOpen(false);
    }
  };

  const handleChangePassword = () => {
    setIsChangePasswordModalOpen(true);
    setIsDropdownOpen(false);
    setPasswordError('');
    setPasswordForm({ currentPassword: '', newPassword: '' });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setPasswordError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${backendUrl}/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setIsChangePasswordModalOpen(false);
        setPasswordForm({ currentPassword: '', newPassword: '' });
        alert('Password changed successfully!');
      } else {
        setPasswordError(data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Change password error:', error);
      setPasswordError('An error occurred while changing password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <>
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <h1>FracFocus Generator</h1>
          </div>
          
          <nav className="header-nav">
            <RoleBasedAccess allowedRoles={['Editor', 'Manager']}>
              <NavLink 
                to="/xml-creator" 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                <span className="nav-icon">📝</span>
                XML Creator
              </NavLink>
            </RoleBasedAccess>
            
            <NavLink 
              to="/xml-viewer" 
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              <span className="nav-icon">👁️</span>
              XML Viewer
            </NavLink>
            
            <RoleBasedAccess allowedRoles={['Manager', 'Editor']}>
              <NavLink 
                to="/settings" 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                <span className="nav-icon">⚙️</span>
                Settings
              </NavLink>
            </RoleBasedAccess>
          </nav>
          
          <div className="header-info">
            {auth.user ? (
              <div className="user-dropdown" ref={dropdownRef}>
                <button 
                  className="user-dropdown-button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span className="user-name">{auth.user.name || 'User'}</span>
                  <span className="dropdown-arrow">▼</span>
                </button>
                
                {isDropdownOpen && (
                  <div className="dropdown-menu">
                    <button 
                      className="dropdown-item"
                      onClick={handleChangePassword}
                    >
                      Change Password
                    </button>
                    <button 
                      className="dropdown-item"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <span>v1.0.0</span>
            )}
          </div>
        </div>
      </header>

      <Modal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        title="Change Password"
      >
        <form onSubmit={handlePasswordSubmit} className="change-password-form">
          <div className="form-group">
            <label>Full name</label>
            <input 
              type="text" 
              value={auth.user?.name || ''} 
              disabled 
              className="form-input disabled"
            />
          </div>
          
          <div className="form-group">
            <label>Email</label>
            <input 
              type="email" 
              value={auth.user?.email || ''} 
              disabled 
              className="form-input disabled"
            />
          </div>
          
          <div className="form-group">
            <label>Current Password</label>
            <input 
              type="password" 
              placeholder="Enter your current password"
              value={passwordForm.currentPassword}
              onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
              required
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label>New Password</label>
            <input 
              type="password" 
              placeholder="Enter your new password"
              value={passwordForm.newPassword}
              onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
              required
              className="form-input"
            />
          </div>
          
          {passwordError && (
            <div className="error-message">{passwordError}</div>
          )}
          
          <button 
            type="submit" 
            className="update-password-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </Modal>
    </>
  );
};

export default NewHeader;
